"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Link, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { format, toZonedTime } from "date-fns-tz";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { CoachMarker } from "@/components/CoachMarker";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import GenericLoader from "@/components/GenericLoader";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import useUserStore from "@/features/auth/store/useUserStore";
import PATHS from "@/routes/paths";
import { WorkspaceListItem, WorkspacesListResponse } from "@/features/workspaces/types/workspacesList";
import { cn } from "@/lib/utils";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale";

const WorkspacesListPage = () => {
  const { t } = useTranslation(["workspaces", "common", "help"]);
  const [workspaces, setWorkspaces] = useState<WorkspacesListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { user } = useUserStore();
  const navigate = useNavigate();
  const isTutor = user?.role === "tutor";

  const fetchWorkspaces = async (page: number = 1, search: string = "") => {
    // Only show full loading state on initial load
    if (isInitialLoad) {
      setIsLoading(true);
    } else {
      setIsSearching(true);
    }
    setError(null);

    try {
      const endpoint = isTutor
        ? API_ENDPOINTS.WORKSPACES.TUTOR_LIST
        : API_ENDPOINTS.WORKSPACES.STUDENT_LIST;

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: "20",
      });

      if (search.trim().length >= 2) {
        params.append("search", search.trim());
      }

      const { data } = await axiosApiInstance.get<WorkspacesListResponse>(
        `${endpoint}?${params.toString()}`,
      );

      setWorkspaces(data);
      setIsInitialLoad(false);
    } catch (err: any) {
      console.error("Error fetching workspaces:", err);

      // Handle specific API errors
      if (err.response?.status === 401) {
        setError(t("common:errors.unauthenticated"));
      } else if (err.response?.status === 403) {
        setError(t("common:errors.permissionDenied"));
      } else if (err.response?.status === 422) {
        // Handle validation errors
        const validationErrors = err.response?.data?.errors;

        if (validationErrors?.search) {
          setError("Search term must be at least 2 characters");
        } else if (validationErrors?.per_page) {
          setError("Too many items per page");
        } else {
          setError("Invalid request parameters");
        }
      } else {
        setError("Failed to load workspaces");
      }
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const debouncedFetchWorkspaces = useDebouncedCallback(fetchWorkspaces, 500);

  const debouncedSearch = useDebouncedCallback((query: string) => {
    setCurrentPage(1);
    debouncedFetchWorkspaces(1, query);
  }, 1000);

  // Initial load effect
  useEffect(() => {
    fetchWorkspaces(1, "");
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;

    setSearchQuery(query);

    if (query.trim().length > 0) {
      setIsSearching(true);
    }

    debouncedSearch(query);
  };

  const handleWorkspaceClick = (workspace: WorkspaceListItem) => {
    const basePath = isTutor
      ? PATHS.TUTOR.WORKSPACES.WORKSPACES
      : PATHS.STUDENT.WORKSPACES.WORKSPACES;

    navigate(`${basePath}/${workspace.id}`);
  };

  const handleChatClick = (e: React.MouseEvent, workspaceId: string, conversationId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const chatPath = `/${isTutor ? "tutor" : "student"}/workspaces/${workspaceId}/chat/${conversationId}`;

    navigate(chatPath);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    setIsSearching(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    debouncedFetchWorkspaces(page, searchQuery);
  };

  // Render loading skeleton
  if (isLoading && !workspaces) {
    return (
      <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="mx-auto max-w-6xl p-4 md:p-6">
            {/* Hero Header Skeleton */}
            <div className="bg-side-accent/50 relative mb-6 overflow-visible rounded-2xl border-none p-4 md:p-8">
              <div className="relative">
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-4 md:gap-6">
                    <Skeleton className="h-12 w-12 rounded-2xl md:h-16 md:w-16" />
                    <div>
                      <Skeleton className="mb-2 h-6 w-32 md:h-8 md:w-48" />
                      <Skeleton className="h-4 w-48 md:h-5 md:w-64" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full md:w-64" />
                </div>
              </div>
            </div>
            {/* Workspace Cards Skeleton */}
            <div className="space-y-3 md:space-y-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-2xl md:h-24" />
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="mx-auto max-w-6xl p-4 md:p-6">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-foreground/50 flex h-80 flex-col items-center justify-center rounded-2xl backdrop-blur-xl md:h-96"
              initial={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-muted/20 mb-6 flex h-16 w-16 items-center justify-center rounded-2xl md:h-20 md:w-20">
                <Icon
                  className="text-muted-foreground"
                  height={"2rem"}
                  icon="tabler:alert-triangle"
                  width={"2rem"}
                />
              </div>
              <h2 className="text-accent-foreground mb-2 text-lg font-semibold md:text-xl">
                {t("common:errors.genericErrorTitle")}
              </h2>
              <p className="text-muted-foreground mb-6 text-center text-sm md:text-base">{error}</p>
              <Button onClick={() => fetchWorkspaces(currentPage, searchQuery)}>
                {t("common:retry")}
              </Button>
            </motion.div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="mx-auto max-w-6xl p-4 md:p-6">
          <title>{`${t("workspaces:workspacesList")} - Ximly`}</title>

          {/* Hero Header */}
          <CoachMarker
            description={t(
              `help:coachMarkers.workspacesOverview.${isTutor ? "tutor" : "student"}.description` as any,
            )}
            id="workspaces-overview"
            placement="top"
            title={t(
              `help:coachMarkers.workspacesOverview.${isTutor ? "tutor" : "student"}.title` as any,
            )}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-side-accent/50 text-accent-foreground relative mb-6 rounded-2xl border-none p-4 shadow-2xl md:mb-8 md:p-8"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute -top-10 -left-10 h-24 w-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 opacity-5 md:-top-20 md:-left-20 md:h-40 md:w-40" />
                <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-gradient-to-tr from-green-400 to-teal-500 opacity-5 md:-right-16 md:-bottom-16 md:h-32 md:w-32" />
              </div>

              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-4 md:gap-6">
                    <motion.div
                      animate={{ scale: 1 }}
                      className="bg-muted/20 flex items-center justify-center rounded-2xl p-2 backdrop-blur-sm md:p-4"
                      initial={{ scale: 0.8 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <Icon
                        className="text-accent-foreground"
                        height="1.5rem"
                        icon="mdi:view-dashboard"
                        width="1.5rem"
                      />
                    </motion.div>
                    <div>
                      <h1 className="text-accent-foreground text-2xl leading-tight font-bold md:text-3xl">
                        {t("workspaces:workspacesList")}
                      </h1>
                      <p className="text-muted-foreground mt-1 text-sm md:mt-2 md:text-base">
                        {t("workspaces:workspacesListDescription")}
                      </p>
                    </div>
                  </div>

                  {/* Search Form */}
                  <div className="my-auto w-full md:max-w-md">
                    <div className="relative">
                      <Input
                        className="pr-16 md:pr-20"
                        placeholder={t("workspaces:searchWorkspaces")}
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                      <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
                        {searchQuery && (
                          <Button
                            className="h-6 w-6 p-0"
                            size="sm"
                            type="button"
                            variant="ghost"
                            onClick={clearSearch}
                          >
                            <Icon height="1rem" icon="tabler:x" width="1rem" />
                          </Button>
                        )}
                        <div className="flex h-6 w-6 items-center justify-center">
                          {isSearching ? (
                            <Spinner className="h-4 w-4" />
                          ) : (
                            <Icon height="1rem" icon="tabler:search" width="1rem" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </CoachMarker>

          {/* Workspaces List */}
          {isLoading || isSearching ? (
            <div className="relative min-h-[400px]">
              <GenericLoader />
            </div>
          ) : workspaces && workspaces.data.length === 0 ? (
            <CoachMarker
              description={t(
                `help:coachMarkers.noWorkspacesState.${isTutor ? "tutor" : "student"}.description` as any,
              )}
              id="no-workspaces-state"
              placement="top"
              title={t(
                `help:coachMarkers.noWorkspacesState.${isTutor ? "tutor" : "student"}.title` as any,
              )}
            >
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="bg-foreground/50 relative rounded-2xl border-none p-8 text-center shadow-2xl backdrop-blur-xl md:p-12"
                initial={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                  <div className="absolute -top-8 -right-8 h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 opacity-5 md:-top-16 md:-right-16 md:h-32 md:w-32" />
                  <div className="absolute -bottom-6 -left-6 h-16 w-16 rounded-full bg-gradient-to-tr from-orange-400 to-red-500 opacity-5 md:-bottom-12 md:-left-12 md:h-24 md:w-24" />
                </div>

                <div className="relative">
                  <div className="bg-muted/20 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl backdrop-blur-sm md:h-20 md:w-20">
                    <Icon
                      className="text-muted-foreground"
                      height="2rem"
                      icon="mdi:folder-open-outline"
                      width="2rem"
                    />
                  </div>
                  <h2 className="text-accent-foreground mb-3 text-xl font-semibold md:text-2xl">
                    {searchQuery
                      ? t("workspaces:noSearchResults")
                      : t("workspaces:noWorkspacesAvailable")}
                  </h2>
                  <p className="text-muted-foreground mx-auto mb-6 max-w-md text-sm md:mb-8 md:text-base">
                    {searchQuery
                      ? t("workspaces:noSearchResultsDescription", { query: searchQuery })
                      : t("workspaces:noWorkspacesDescription")}
                  </p>
                  {searchQuery && (
                    <Button
                      className="bg-accent-foreground/90 hover:bg-accent-foreground text-primary"
                      size={"lg"}
                      onClick={clearSearch}
                    >
                      <Icon height={"1rem"} icon="tabler:x" width={"1rem"} />
                      {t("common:clearSearch")}
                    </Button>
                  )}
                </div>
              </motion.div>
            </CoachMarker>
          ) : (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {/* Results Info */}
              {workspaces && (
                <div className="mb-4 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-muted/30 text-muted-foreground" variant="outline">
                      {t("workspaces:totalWorkspaces", { count: workspaces.meta.total })}
                    </Badge>
                    {searchQuery && (
                      <Badge variant="outline">
                        {t("workspaces:searchResults", { query: searchQuery })}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Workspaces List - Individual Cards */}
              <div className="space-y-2">
                {workspaces?.data.map((workspace, index) => (
                  <motion.div
                    key={workspace.id}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                  >
                    <div
                      className="hover:bg-muted/20 group relative cursor-pointer overflow-hidden p-4 transition-all duration-300 md:p-6"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleWorkspaceClick(workspace)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleWorkspaceClick(workspace);
                        }
                      }}
                    >
                      {/* Background blur effect */}
                      <div className="absolute inset-0 top-0 -left-4 h-36 w-36 -translate-x-1/4 -translate-y-1/2 rounded-full opacity-20 md:top-1/2 md:-left-8 md:h-32 md:w-32 xl:h-40 xl:w-40">
                        <img
                          alt={workspace.name}
                          className="h-full w-full object-cover blur-2xl"
                          src={workspace.avatar || "/placeholder.svg?height=40&width=40"}
                        />
                      </div>

                      <div className="relative">
                        {/* Mobile Layout */}
                        <div className="flex flex-col gap-3 md:hidden">
                          <div className="flex items-start gap-3">
                            {/* Workspace Icon */}
                            <div className="bg-muted/30 flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg backdrop-blur-sm">
                              <img
                                alt={workspace.name}
                                className="h-full w-full object-cover"
                                src={workspace.avatar || "/placeholder.svg?height=40&width=40"}
                              />
                            </div>

                            {/* Workspace Info */}
                            <div className="mb-1 flex min-w-0 flex-1 flex-col justify-center gap-1">
                              <div className="mb-0.5 flex items-center gap-2">
                                <h3 className="text-accent-foreground truncate text-base font-semibold">
                                  {workspace.name}
                                </h3>
                                <Badge
                                  className={`bg-accent-foreground/75 text-primary rounded-full border-none px-2 py-0.5 text-xs`}
                                  variant="outline"
                                >
                                  {t(`workspaces:workspaceType.${workspace.workspace_type}`)}
                                </Badge>
                              </div>
                              {/* Next Lesson Info - Mobile */}
                              {workspace.next_lesson ? (
                                <Link
                                  className="flex w-full"
                                  to={`/${isTutor ? "tutor" : "student"}/workspaces/${workspace.id}/lessons/${workspace.next_lesson.id}/details`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="bg-accent/20 hover:bg-accent/30 border-accent/30 flex w-full gap-1 rounded-lg border px-2 py-1.5 shadow-sm transition-all duration-300 max-sm:flex-col">
                                    <div className="flex items-center gap-1">
                                      <Icon
                                        className="text-accent-foreground"
                                        height="0.75rem"
                                        icon="tabler:calendar"
                                        width="0.75rem"
                                      />
                                      <span className="text-accent-foreground text-xs font-medium">
                                        {t("workspaces:nextLesson.title")}
                                      </span>
                                    </div>
                                    <span className="text-accent-foreground/80 text-xs">
                                      {format(
                                        toZonedTime(
                                          workspace.next_lesson.start_time_utc,
                                          user?.timezone ?? "UTC",
                                        ),
                                        "PPPp",
                                        { locale: currentLanguageLocale() },
                                      )}
                                    </span>
                                  </div>
                                </Link>
                              ) : (
                                <div className="bg-muted/20 inline-flex flex-col items-center gap-1 rounded px-2 py-1">
                                  <div className="flex items-center gap-1">
                                    <Icon
                                      className="text-muted-foreground"
                                      height="0.75rem"
                                      icon="tabler:calendar-off"
                                      width="0.75rem"
                                    />
                                    <span className="text-muted-foreground text-xs">
                                      {t("workspaces:noUpcomingLessons")}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Bottom Action Buttons */}
                          <div className="flex gap-2">
                            <Button
                              className="bg-accent-foreground/75 flex-1 rounded-xl"
                              size="sm"
                              variant="foreground"
                              onClick={() => handleWorkspaceClick(workspace)}
                            >
                              {t("workspaces:goToWorkspace")}
                            </Button>
                            {workspace.conversation_id && (
                              <Button
                                className="h-9 w-9 rounded-xl"
                                size="icon"
                                variant="outline"
                                onClick={(e) =>
                                  handleChatClick(e, workspace.id, workspace.conversation_id)
                                }
                              >
                                <Icon
                                  height="1rem"
                                  icon="tabler:message-circle-filled"
                                  width="1rem"
                                />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:flex md:items-start">
                          <div className="flex flex-1 items-start gap-4">
                            {/* Workspace Icon */}
                            <div className="bg-muted/30 flex size-15 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl backdrop-blur-sm">
                              <img
                                alt={workspace.name}
                                className="h-full w-full object-cover"
                                src={workspace.avatar || "/placeholder.svg?height=48&width=48"}
                              />
                            </div>

                            {/* 2x2 Grid Layout for Desktop */}
                            <div className="grid flex-1 grid-cols-2 gap-1">
                              {/* Row 1, Col 1: Workspace Name */}
                              <div className="flex items-center">
                                <h3 className="text-accent-foreground truncate text-lg font-semibold">
                                  {workspace.name}
                                </h3>
                              </div>

                              {/* Row 1, Col 2: Chat Button + Three Dots */}
                              <div className="flex items-center justify-end gap-2">
                                {workspace.conversation_id && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          className="h-8 w-8 rounded-lg"
                                          size="icon"
                                          variant="ghost"
                                          onClick={(e) =>
                                            handleChatClick(
                                              e,
                                              workspace.id,
                                              workspace.conversation_id,
                                            )
                                          }
                                        >
                                          <Icon
                                            height="1rem"
                                            icon="tabler:message-circle-filled"
                                            width="1rem"
                                          />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side={"left"}>
                                        <p>{t("workspaces:openChat")}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        className="h-8 w-8 rounded-lg"
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => handleWorkspaceClick(workspace)}
                                      >
                                        <Icon height="1rem" icon="mdi:eye" width="1rem" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side={"left"}>
                                      <p>{t("workspaces:goToWorkspace")}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>

                              {/* Row 2, Col 1: Workspace Type Badge */}
                              <div className="flex items-center">
                                <Badge
                                  className={`bg-accent-foreground/75 text-primary rounded-full border-none px-3 py-1 text-xs`}
                                  variant="outline"
                                >
                                  {t(`workspaces:workspaceType.${workspace.workspace_type}`)}
                                </Badge>
                              </div>

                              {/* Row 2, Col 2: Upcoming Lesson Card */}
                              <div className="flex items-center justify-end">
                                {workspace.next_lesson ? (
                                  <Link
                                    className="inline-block w-fit"
                                    to={`/${isTutor ? "tutor" : "student"}/workspaces/${workspace.id}/lessons/${workspace.next_lesson.id}/details`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="bg-accent/20 hover:bg-accent/30 border-accent/30 flex items-center gap-2 rounded-lg border px-3 py-1.5 shadow-sm transition-all duration-300">
                                      <Icon
                                        className="text-accent-foreground"
                                        height="0.75rem"
                                        icon="tabler:calendar"
                                        width="0.75rem"
                                      />
                                      <span className="text-accent-foreground text-xs font-medium">
                                        {t("workspaces:nextLesson.title")}
                                      </span>
                                      <span className="text-accent-foreground/80 text-xs">
                                        {format(
                                          toZonedTime(
                                            workspace.next_lesson.start_time_utc,
                                            user?.timezone ?? "UTC",
                                          ),
                                          "PPPp",
                                          { locale: currentLanguageLocale() },
                                        )}
                                      </span>
                                      <Icon
                                        className="text-accent-foreground/60"
                                        height="0.75rem"
                                        icon="tabler:arrow-right"
                                        width="0.75rem"
                                      />
                                    </div>
                                  </Link>
                                ) : (
                                  <div className="bg-muted/20 inline-flex w-fit items-center gap-2 rounded px-2 py-1">
                                    <Icon
                                      className="text-muted-foreground"
                                      height="0.75rem"
                                      icon="tabler:calendar-off"
                                      width="0.75rem"
                                    />
                                    <span className="text-muted-foreground text-xs">
                                      {t("workspaces:noUpcomingLessons")}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {workspaces && workspaces.meta.last_page > 1 && (
                <div className="flex items-center justify-center gap-4 py-8">
                  <div className="flex items-center gap-2">
                    {/* Previous Page */}
                    {workspaces.meta.current_page > 1 && (
                      <Button
                        className="h-10 px-4 py-2"
                        variant="outline"
                        onClick={() => handlePageChange(workspaces.meta.current_page - 1)}
                      >
                        <Icon
                          className="mr-2"
                          height="1rem"
                          icon="tabler:chevron-left"
                          width="1rem"
                        />
                        {t("common:previousPage")}
                      </Button>
                    )}

                    {/* Page Numbers */}
                    {(() => {
                      const currentPage = workspaces.meta.current_page;
                      const lastPage = workspaces.meta.last_page;
                      const pages = [];

                      // Show first page if we're not close to it
                      if (currentPage > 3) {
                        pages.push(
                          <Button
                            key={1}
                            className="h-10 w-10 p-0"
                            variant="outline"
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </Button>,
                        );

                        if (currentPage > 4) {
                          pages.push(
                            <span key="start-ellipsis" className="text-muted-foreground px-2">
                              ...
                            </span>,
                          );
                        }
                      }

                      // Show pages around current page
                      const start = Math.max(1, currentPage - 2);
                      const end = Math.min(lastPage, currentPage + 2);

                      for (let i = start; i <= end; i++) {
                        const isCurrentPage = i === currentPage;

                        pages.push(
                          <Button
                            key={i}
                            className={cn(
                              "h-10 w-10 p-0",
                              isCurrentPage && "bg-accent text-accent-foreground",
                            )}
                            variant={isCurrentPage ? "default" : "outline"}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </Button>,
                        );
                      }

                      // Show last page if we're not close to it
                      if (currentPage < lastPage - 2) {
                        if (currentPage < lastPage - 3) {
                          pages.push(
                            <span key="end-ellipsis" className="text-muted-foreground px-2">
                              ...
                            </span>,
                          );
                        }

                        pages.push(
                          <Button
                            key={lastPage}
                            className="h-10 w-10 p-0"
                            variant="outline"
                            onClick={() => handlePageChange(lastPage)}
                          >
                            {lastPage}
                          </Button>,
                        );
                      }

                      return pages;
                    })()}

                    {/* Next Page */}
                    {workspaces.meta.current_page < workspaces.meta.last_page && (
                      <Button
                        className="h-10 px-4 py-2"
                        variant="outline"
                        onClick={() => handlePageChange(workspaces.meta.current_page + 1)}
                      >
                        {t("common:nextPage")}
                        <Icon
                          className="ml-2"
                          height="1rem"
                          icon="tabler:chevron-right"
                          width="1rem"
                        />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default WorkspacesListPage;
